import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { customersApi, serialNumbersApi, serviceReportsApi, customerDocumentsApi, usersApi, teamsApi, tasksApi, triggerBlobDownload, placetelFilename, PLACETEL_HINT } from "@/lib/api";
import api from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { ScrollFade } from "@/components/ScrollFade";
import { useToast } from "@/components/SimpleToast";
import { StatusBadge } from "@/components/data-table/StatusBadge";
import {
  ArrowLeft, User, Hash, FileText, StickyNote, MessageSquare, FolderOpen, LayoutGrid,
  Pencil, Plus, MoreVertical, Trash2, MapPin, Phone, Mail, Wrench, Building2, Clock, PanelRight, ChevronRight, UserCog, Users2, History,
} from "lucide-react";
import DocumentUpload from "@/components/DocumentUpload";
import { CustomerSerialNumbers } from "@/components/customer/CustomerSerialNumbers";
import { CustomerServiceReports } from "@/components/customer/CustomerServiceReports";
import { CustomerNotesTab } from "@/components/customer/CustomerNotesTab";
import { CustomerConversationTab } from "@/components/customer/CustomerConversationTab";
import { CustomerContactsTab } from "@/components/customer/CustomerContactsTab";
import { CustomerFormDialog } from "@/components/customer/CustomerFormDialog";
import { CustomerCommentsTab } from "@/components/customer/CustomerCommentsTab";
import { CustomerAuditTab } from "@/components/customer/CustomerAuditTab";
import { CustomerTimeline } from "@/components/customer/CustomerTimeline";
import { CustomerMedicalSoftwareCard } from "@/components/customer/CustomerMedicalSoftwareCard";
import { SystemBadges } from "@/components/customer/SystemChipsField";
import { TaskDialog } from "@/components/tasks/TaskDialog";

const CATEGORIES = ["Kunde", "Interessent", "Lieferant"];
const fmtDate = (s) => { if (!s) return "—"; const d = new Date(s); return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }); };

const TAB_TRIGGER = "shrink-0 px-3 py-2 border border-border rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary";

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [customer, setCustomer] = useState(null);
  const [serialNumbers, setSerialNumbers] = useState([]);
  const [reports, setReports] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [nextTask, setNextTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    usersApi.getAll().then((r) => setUsers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    teamsApi.getAll().then((r) => setTeams(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  // contacts
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editContactIndex, setEditContactIndex] = useState(null);
  const [saving, setSaving] = useState(false);
  const [contactForm, setContactForm] = useState({ salutation: "", title: "", first_name: "", name: "", position: "", phone: "", email: "" });

  // notes
  const [savingNotes, setSavingNotes] = useState(false);
  const [notes, setNotes] = useState("");
  const [savingGeneralNotes, setSavingGeneralNotes] = useState(false);

  // Wiedervorlage (Folgeaufgabe) aus Gesprächsnotiz
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskSaving, setTaskSaving] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: "", description: "", due_date: "", priority: "Mittel", status: "Offen", assigned_to_user_id: "", customer_id: "" });

  // full form edit
  const [editCustomerDialogOpen, setEditCustomerDialogOpen] = useState(false);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [customerForm, setCustomerForm] = useState({ company_name: "", category: "Kunde", has_maintenance_contract: false, systems: ["Quincy"], address: "", postal_code: "", city: "", phone: "", phone_extension: "", email: "", phones: [], emails: [], notes: "", assigned_to: "", team_id: "" });

  // sidepanel quick edit
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [quickForm, setQuickForm] = useState({ address: "", postal_code: "", city: "", phone: "", phone_extension: "", email: "" });
  const [savingQuick, setSavingQuick] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [customerRes, serialsRes, reportsRes, docsRes] = await Promise.all([
        customersApi.getById(id),
        serialNumbersApi.getAll(null, id),
        serviceReportsApi.getAll(null, id),
        customerDocumentsApi.getAll(id),
      ]);
      setCustomer(customerRes.data);
      setSerialNumbers(Array.isArray(serialsRes.data) ? serialsRes.data : []);
      setReports(Array.isArray(reportsRes.data) ? reportsRes.data : []);
      setDocuments(Array.isArray(docsRes.data) ? docsRes.data : []);
      setNotes(customerRes.data.notes || "");
      // Nächste offene Aufgabe (frühestes Fälligkeitsdatum) zu diesem Unternehmen
      try {
        const tRes = await tasksApi.getByCustomer(id);
        const open = (Array.isArray(tRes.data) ? tRes.data : []).filter((t) => t.status !== "Erledigt");
        const withDue = open.filter((t) => t.due_date).sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
        setNextTask(withDue[0] || open[0] || null);
      } catch { setNextTask(null); }
    } catch (error) { navigate("/customers"); } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Inline / schnelles Update einzelner Felder (optimistisch)
  const quickUpdate = async (patch, successMsg = "Aktualisiert") => {
    const prev = customer;
    const next = { ...customer, ...patch };
    setCustomer(next);
    try { const res = await customersApi.update(id, next); setCustomer(res.data); toast.success(successMsg); }
    catch { setCustomer(prev); toast.error("Fehler beim Speichern"); }
  };

  const handleConflict = (e) => {
    if (e?.response?.status === 409) {
      toast.error(e.response.data?.detail || "Der Datensatz wurde zwischenzeitlich geändert. Neu geladen.");
      fetchData();
      return true;
    }
    return false;
  };

  const handleOpenEditCustomer = () => {
    setCustomerForm({
      company_name: customer.company_name || "", category: customer.category || "Kunde",
      has_maintenance_contract: customer.has_maintenance_contract || false, systems: (customer.systems && customer.systems.length) ? customer.systems : ["Quincy"],
      address: customer.address || "", postal_code: customer.postal_code || "", city: customer.city || "",
      phone: customer.phone || "", phone_extension: customer.phone_extension || "", email: customer.email || "",
      phones: (customer.phones && customer.phones.length) ? customer.phones : (customer.phone ? [{ label: "", value: customer.phone }] : []),
      emails: (customer.emails && customer.emails.length) ? customer.emails : (customer.email ? [{ label: "", value: customer.email }] : []),
      notes: customer.notes || "", assigned_to: customer.assigned_to || "", team_id: customer.team_id || "",
    });
    setEditCustomerDialogOpen(true);
  };
  const handleSaveCustomer = async (e) => {
    e.preventDefault(); setSavingCustomer(true);
    try {
      await customersApi.update(id, { ...customerForm, contacts: customer.contacts || [], conversation_notes: customer.conversation_notes, conversation_notes_datetime: customer.conversation_notes_datetime, conversation_notes_datetime_end: customer.conversation_notes_datetime_end }, customer.updated_at);
      toast.success("Unternehmen aktualisiert"); setEditCustomerDialogOpen(false); fetchData();
    } catch (err) { if (handleConflict(err)) { setEditCustomerDialogOpen(false); } else { toast.error(err.response?.data?.detail || "Fehler beim Speichern"); } } finally { setSavingCustomer(false); }
  };

  const openSidePanel = () => {
    setQuickForm({ address: customer.address || "", postal_code: customer.postal_code || "", city: customer.city || "", phone: customer.phone || "", phone_extension: customer.phone_extension || "", email: customer.email || "" });
    setSidePanelOpen(true);
  };
  const handleSaveQuick = async () => {
    setSavingQuick(true);
    // Primär-Eintrag der wiederholbaren Listen mit Schnellbearbeitung synchronisieren
    const syncPrimary = (list, val) => {
      const arr = Array.isArray(list) ? [...list] : [];
      if (val) {
        if (arr.length) arr[0] = { ...arr[0], value: val };
        else arr.push({ label: "", value: val });
      }
      return arr;
    };
    const payload = {
      ...customer, ...quickForm,
      phones: syncPrimary(customer.phones, quickForm.phone),
      emails: syncPrimary(customer.emails, quickForm.email),
    };
    try { const res = await customersApi.update(id, payload, customer.updated_at); setCustomer(res.data); toast.success("Kontaktdaten gespeichert"); setSidePanelOpen(false); }
    catch (err) { if (handleConflict(err)) { setSidePanelOpen(false); } else { toast.error(err.response?.data?.detail || "Fehler beim Speichern"); } } finally { setSavingQuick(false); }
  };

  // contacts
  const handleOpenContactDialog = (contact, index) => {
    if (contact) { setEditContactIndex(index); setContactForm({ salutation: contact.salutation || "", title: contact.title || "", first_name: contact.first_name || "", name: contact.name || "", position: contact.position || "", phone: contact.phone || "", email: contact.email || "" }); }
    else { setEditContactIndex(null); setContactForm({ salutation: "", title: "", first_name: "", name: "", position: "", phone: "", email: "" }); }
    setContactDialogOpen(true);
  };
  const handleSaveContact = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const contacts = customer.contacts ? [...customer.contacts] : [];
      if (editContactIndex !== null) contacts[editContactIndex] = contactForm; else contacts.push(contactForm);
      await customersApi.update(id, { ...customer, contacts });
      toast.success(editContactIndex !== null ? "Kontakt aktualisiert" : "Kontakt hinzugefügt"); setContactDialogOpen(false); fetchData();
    } catch { toast.error("Fehler beim Speichern"); } finally { setSaving(false); }
  };
  const handleDeleteContact = async (index) => {
    try { const contacts = customer.contacts.filter((_, i) => i !== index); await customersApi.update(id, { ...customer, contacts }); toast.success("Kontakt gelöscht"); fetchData(); }
    catch { toast.error("Fehler beim Löschen"); }
  };
  const handleExportContactsPlacetel = async (contactIndex = null) => {
    try {
      const params = contactIndex !== null ? { contact_index: contactIndex } : {};
      const response = await customersApi.exportCustomerPlacetel(id, params);
      triggerBlobDownload(response.data, placetelFilename());
      toast.success(PLACETEL_HINT, { duration: 8000 });
    } catch { toast.error("Placetel-Export fehlgeschlagen"); }
  };

  const handleAddConversationNote = async (payload) => {
    setSavingNotes(true);
    try { const res = await customersApi.addConversationNote(id, payload); setCustomer(res.data); toast.success("Gesprächsnotiz gespeichert"); return true; }
    catch { toast.error("Fehler beim Speichern"); return false; } finally { setSavingNotes(false); }
  };
  const handleUpdateConversationNote = async (entryId, payload) => {
    setSavingNotes(true);
    try { const res = await customersApi.updateConversationNote(id, entryId, payload); setCustomer(res.data); toast.success("Gesprächsnotiz aktualisiert"); return true; }
    catch { toast.error("Fehler beim Speichern"); return false; } finally { setSavingNotes(false); }
  };
  const handleDeleteConversationNote = async (entryId) => {
    try { const res = await customersApi.deleteConversationNote(id, entryId); setCustomer(res.data); toast.success("Gesprächsnotiz gelöscht"); }
    catch { toast.error("Fehler beim Löschen"); }
  };
  const openFollowupTask = (noteDate) => {
    const base = noteDate ? new Date(noteDate) : new Date();
    const due = isNaN(base.getTime()) ? new Date() : base;
    due.setDate(due.getDate() + 7);
    setTaskForm({
      title: `Wiedervorlage: ${customer?.company_name || ""}`,
      description: "",
      due_date: due.toISOString().split("T")[0],
      priority: "Mittel",
      status: "Offen",
      assigned_to_user_id: "",
      customer_id: id,
      is_followup: true,
    });
    setTaskDialogOpen(true);
  };
  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    setTaskSaving(true);
    try {
      await api.post("/tasks", taskForm);
      toast.success("Wiedervorlage erstellt");
      setTaskDialogOpen(false);
      window.dispatchEvent(new Event("tasks-changed"));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern der Aufgabe");
    } finally { setTaskSaving(false); }
  };
  const handleSaveGeneralNotes = async () => {
    setSavingGeneralNotes(true);
    try { await customersApi.update(id, { ...customer, notes }); toast.success("Notizen gespeichert"); fetchData(); }
    catch { toast.error("Fehler beim Speichern"); } finally { setSavingGeneralNotes(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Unternehmen „${customer.company_name}" wirklich löschen?`)) return;
    try { await customersApi.delete(id); toast.success("Unternehmen gelöscht"); navigate("/customers"); }
    catch { toast.error("Fehler beim Löschen"); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[50vh]"><div className="spinner" /></div>;
  if (!customer) return null;

  const contacts = customer.contacts || [];
  const initials = (customer.company_name || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  const phoneList = (customer.phones && customer.phones.length) ? customer.phones : (customer.phone ? [{ label: "", value: customer.phone }] : []);
  const emailList = (customer.emails && customer.emails.length) ? customer.emails : (customer.email ? [{ label: "", value: customer.email }] : []);
  const assignedName = users.find((u) => u.email === customer.assigned_to)?.full_name;
  const conversationEntries = customer.conversation_note_entries || [];
  const lastContactDate = conversationEntries.length
    ? [...conversationEntries].map((e) => e.datetime_start || e.created_at).filter(Boolean).sort().reverse()[0]
    : customer.conversation_notes_datetime;

  const InfoRow = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-3 py-2">
      <Icon className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium break-words">{value || <span className="text-muted-foreground font-normal">—</span>}</p>
      </div>
    </div>
  );

  const LinkStat = ({ icon: Icon, label, count, onClick }) => (
    <button onClick={onClick} className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg border border-border hover:bg-accent transition-colors text-left" data-testid={`linkstat-${label}`}>
      <span className="flex items-center gap-2 text-sm"><Icon className="w-4 h-4 text-muted-foreground" />{label}</span>
      <span className="flex items-center gap-1 text-sm font-semibold">{count}<ChevronRight className="w-4 h-4 text-muted-foreground" /></span>
    </button>
  );

  return (
    <div className="space-y-5 animate-fade-in" data-testid="customer-detail">
      <nav className="flex items-center gap-1.5 text-sm" aria-label="Breadcrumb">
        <button onClick={() => navigate("/customers")} className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="back-btn"><ArrowLeft className="w-4 h-4" />Kunden</button>
        <ChevronRight className="w-4 h-4 text-muted-foreground/50" />
        <span className="font-medium truncate max-w-[300px]">{customer.company_name}</span>
      </nav>

      {/* Kopfbereich */}
      <div className="rounded-xl border border-border bg-card p-5 sm:p-6" data-testid="detail-header">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-primary/10 text-primary flex items-center justify-center text-lg font-bold shrink-0">{initials}</div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold tracking-tight truncate">{customer.company_name}</h1>
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              <StatusBadge status={customer.category || "Kunde"} data-testid="header-category-badge" />
              {customer.has_maintenance_contract && <StatusBadge status="aktiv" label="Wartungsvertrag" tone="success" icon={Wrench} />}
              <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><Clock className="w-3 h-3" />Aktualisiert {fmtDate(customer.updated_at)}</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 mt-3 text-sm" data-testid="profile-facts">
              {phoneList[0]?.value && <a href={`tel:${phoneList[0].value}`} className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-primary transition-colors"><Phone className="w-4 h-4" />{phoneList[0].value}</a>}
              {emailList[0]?.value && <a href={`mailto:${emailList[0].value}`} className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-primary transition-colors"><Mail className="w-4 h-4" />{emailList[0].value}</a>}
              {(customer.city || customer.postal_code) && <span className="inline-flex items-center gap-1.5 text-muted-foreground"><MapPin className="w-4 h-4" />{[customer.postal_code, customer.city].filter(Boolean).join(" ")}</span>}
              {assignedName && <span className="inline-flex items-center gap-1.5 text-muted-foreground"><UserCog className="w-4 h-4" />{assignedName}</span>}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={handleOpenEditCustomer} data-testid="edit-customer-btn"><Pencil className="w-4 h-4 mr-2" />Bearbeiten</Button>
            <Button size="sm" variant="outline" onClick={openSidePanel} data-testid="quick-edit-btn"><PanelRight className="w-4 h-4 mr-2" />Schnellbearbeitung</Button>
            <Button size="sm" variant="outline" onClick={() => { setTab("contacts"); handleOpenContactDialog(null); }} data-testid="add-contact-quick-btn"><Plus className="w-4 h-4 mr-2" />Kontakt</Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button size="icon" variant="outline" data-testid="detail-more-btn"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate("/tasks?new=1")}><Plus className="w-4 h-4 mr-2" />Aufgabe erstellen</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTab("documents")}><FolderOpen className="w-4 h-4 mr-2" />Dokument hochladen</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleDelete} className="text-destructive focus:text-destructive" data-testid="delete-customer-btn"><Trash2 className="w-4 h-4 mr-2" />Löschen</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <ScrollFade>
          <TabsList className="justify-start w-max max-w-none overflow-x-visible h-auto gap-1 bg-transparent p-0">
            <TabsTrigger value="overview" data-testid="overview-tab" className={TAB_TRIGGER}><LayoutGrid className="w-4 h-4 mr-2" />Übersicht</TabsTrigger>
            <TabsTrigger value="timeline" data-testid="timeline-tab" className={TAB_TRIGGER}><History className="w-4 h-4 mr-2" />Timeline</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="contacts-tab" className={TAB_TRIGGER}><User className="w-4 h-4 mr-2" />Kontakte ({contacts.length})</TabsTrigger>
            <TabsTrigger value="serials" data-testid="serials-tab" className={TAB_TRIGGER}><Hash className="w-4 h-4 mr-2" />Seriennummern ({serialNumbers.length})</TabsTrigger>
            <TabsTrigger value="reports" data-testid="reports-tab" className={TAB_TRIGGER}><FileText className="w-4 h-4 mr-2" />Serviceberichte ({reports.length})</TabsTrigger>
            <TabsTrigger value="documents" data-testid="documents-tab" className={TAB_TRIGGER}><FolderOpen className="w-4 h-4 mr-2" />Dokumente ({documents.length})</TabsTrigger>
            <TabsTrigger value="notes" data-testid="notes-tab" className={TAB_TRIGGER}><StickyNote className="w-4 h-4 mr-2" />Notizen</TabsTrigger>
            <TabsTrigger value="conversation" data-testid="conversation-tab" className={TAB_TRIGGER}><MessageSquare className="w-4 h-4 mr-2" />Gesprächsnotiz</TabsTrigger>
            <TabsTrigger value="comments" data-testid="comments-tab" className={TAB_TRIGGER}><MessageSquare className="w-4 h-4 mr-2" />Kommentare</TabsTrigger>
            <TabsTrigger value="history" data-testid="history-tab" className={TAB_TRIGGER}><History className="w-4 h-4 mr-2" />Verlauf</TabsTrigger>
          </TabsList>
        </ScrollFade>

        {/* Übersicht: Zweispaltenlayout */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="overview-grid">
            <div className="lg:col-span-2 space-y-5">
              <Card>
                <CardHeader className="pb-3"><CardTitle className="text-base">Stammdaten</CardTitle></CardHeader>
                <CardContent className="grid sm:grid-cols-2 gap-x-6 gap-y-0 pt-0">
                  <InfoRow icon={Building2} label="Firma" value={customer.company_name} />
                  <InfoRow icon={LayoutGrid} label="Kategorie" value={customer.category} />
                  <InfoRow icon={Wrench} label="Wartungsvertrag" value={customer.has_maintenance_contract ? "Ja" : "Nein"} />
                  <InfoRow icon={Hash} label="Systeme" value={(customer.systems || []).length ? <SystemBadges names={customer.systems} /> : null} />
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-base">Kontaktdaten</CardTitle>
                  <Button variant="ghost" size="sm" onClick={openSidePanel} data-testid="overview-edit-contact"><Pencil className="w-3.5 h-3.5 mr-1.5" />Bearbeiten</Button>
                </CardHeader>
                <CardContent className="grid sm:grid-cols-2 gap-x-6 gap-y-0 pt-0">
                  <InfoRow icon={MapPin} label="Adresse" value={[customer.address, [customer.postal_code, customer.city].filter(Boolean).join(" ")].filter(Boolean).join(", ")} />
                  <div className="flex items-start gap-3 py-2">
                    <Phone className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                    <div className="min-w-0" data-testid="detail-phones">
                      <p className="text-xs text-muted-foreground">Telefon</p>
                      {phoneList.length ? phoneList.map((p, i) => (
                        <p key={i} className="text-sm font-medium break-words">{p.value}{p.label ? <span className="text-muted-foreground font-normal"> · {p.label}</span> : null}{i === 0 && customer.phone_extension ? <span className="text-muted-foreground font-normal"> -{customer.phone_extension}</span> : null}</p>
                      )) : <p className="text-sm text-muted-foreground">—</p>}
                    </div>
                  </div>
                  <div className="flex items-start gap-3 py-2">
                    <Mail className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                    <div className="min-w-0" data-testid="detail-emails">
                      <p className="text-xs text-muted-foreground">E-Mail</p>
                      {emailList.length ? emailList.map((em, i) => (
                        <p key={i} className="text-sm font-medium break-words"><a href={`mailto:${em.value}`} className="hover:underline">{em.value}</a>{em.label ? <span className="text-muted-foreground font-normal"> · {em.label}</span> : null}</p>
                      )) : <p className="text-sm text-muted-foreground">—</p>}
                    </div>
                  </div>
                </CardContent>
              </Card>
              <CustomerMedicalSoftwareCard customer={customer} onSaved={(medical_software) => setCustomer((c) => ({ ...c, medical_software }))} />
              <Card>
                <CardHeader className="pb-3"><CardTitle className="text-base">Verknüpfte Datensätze</CardTitle></CardHeader>
                <CardContent className="grid sm:grid-cols-3 gap-3 pt-0">
                  <LinkStat icon={Hash} label="Seriennummern" count={serialNumbers.length} onClick={() => setTab("serials")} />
                  <LinkStat icon={FileText} label="Serviceberichte" count={reports.length} onClick={() => setTab("reports")} />
                  <LinkStat icon={FolderOpen} label="Dokumente" count={documents.length} onClick={() => setTab("documents")} />
                </CardContent>
              </Card>
            </div>

            {/* Rechte Seitenleiste */}
            <div className="space-y-5">
              <Card>
                <CardHeader className="pb-3"><CardTitle className="text-base">Status & Klassifizierung</CardTitle></CardHeader>
                <CardContent className="space-y-4 pt-0">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Kategorie</Label>
                    <Select value={customer.category || "Kunde"} onValueChange={(v) => quickUpdate({ category: v }, `Kategorie: ${v}`)}>
                      <SelectTrigger data-testid="inline-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
                    <div className="flex items-center gap-2 text-sm"><Wrench className="w-4 h-4 text-muted-foreground" />Wartungsvertrag</div>
                    <Switch checked={!!customer.has_maintenance_contract} onCheckedChange={(v) => quickUpdate({ has_maintenance_contract: v }, v ? "Wartung aktiviert" : "Wartung entfernt")} data-testid="inline-maintenance-switch" />
                  </div>
                </CardContent>
              </Card>
              <Card data-testid="assignment-card">
                <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><UserCog className="w-4 h-4" />Zuständigkeit</CardTitle></CardHeader>
                <CardContent className="space-y-4 pt-0">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Zuständiger Mitarbeiter</Label>
                    <Select value={customer.assigned_to || "none"} onValueChange={(v) => quickUpdate({ assigned_to: v === "none" ? null : v }, "Zuständigkeit aktualisiert")}>
                      <SelectTrigger data-testid="inline-assigned-select"><SelectValue placeholder="Nicht zugewiesen" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nicht zugewiesen</SelectItem>
                        {users.map((u) => <SelectItem key={u.id} value={u.email} data-testid={`assigned-option-${u.id}`}>{u.full_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground flex items-center gap-1.5"><Users2 className="w-3.5 h-3.5" />Team</Label>
                    <Select value={customer.team_id || "none"} onValueChange={(v) => quickUpdate({ team_id: v === "none" ? null : v }, "Team aktualisiert")}>
                      <SelectTrigger data-testid="inline-team-select"><SelectValue placeholder="Kein Team" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Kein Team</SelectItem>
                        {teams.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`team-option-${t.id}`}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-3"><CardTitle className="text-base">Schnellinfos</CardTitle></CardHeader>
                <CardContent className="pt-0 text-sm space-y-2">
                  <div className="flex justify-between gap-2" data-testid="quickinfo-assigned"><span className="text-muted-foreground">Zuständig</span><span className="text-right truncate">{assignedName || <span className="text-muted-foreground">Nicht zugewiesen</span>}</span></div>
                  <div className="flex justify-between gap-2" data-testid="quickinfo-last-contact"><span className="text-muted-foreground">Letzter Kontakt</span><span className="text-right">{fmtDate(lastContactDate)}</span></div>
                  <div className="flex justify-between gap-2" data-testid="quickinfo-next-task">
                    <span className="text-muted-foreground">Nächste Aufgabe</span>
                    {nextTask
                      ? <button onClick={() => navigate("/tasks")} className="text-right text-primary hover:underline truncate max-w-[60%]" title={nextTask.title}>{nextTask.title}{nextTask.due_date ? ` · ${fmtDate(nextTask.due_date)}` : ""}</button>
                      : <span className="text-right text-muted-foreground">Keine offene Aufgabe</span>}
                  </div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Erstellt</span><span>{fmtDate(customer.created_at)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Aktualisiert</span><span>{fmtDate(customer.updated_at)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Ansprechpartner</span><span>{contacts.length}</span></div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-base">Interne Notizen</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setTab("notes")} data-testid="overview-open-notes"><Pencil className="w-3.5 h-3.5 mr-1.5" />Bearbeiten</Button>
                </CardHeader>
                <CardContent className="pt-0">
                  {notes && notes.replace(/<[^>]*>/g, "").trim()
                    ? <p className="text-sm text-muted-foreground line-clamp-4 whitespace-pre-wrap">{notes.replace(/<[^>]*>/g, "")}</p>
                    : <p className="text-sm text-muted-foreground italic">Keine Notizen hinterlegt.</p>}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="timeline">
          <CustomerTimeline customer={customer} reports={reports} conversationEntries={conversationEntries} />
        </TabsContent>

        <TabsContent value="contacts">
          <CustomerContactsTab contacts={contacts} contactDialogOpen={contactDialogOpen} setContactDialogOpen={setContactDialogOpen} editContactIndex={editContactIndex} contactForm={contactForm} setContactForm={setContactForm} onOpenDialog={handleOpenContactDialog} onSave={handleSaveContact} onDelete={handleDeleteContact} saving={saving} onExportAll={() => handleExportContactsPlacetel(null)} onExportContact={(index) => handleExportContactsPlacetel(index)} canExport={(customer.category || "Kunde") === "Kunde"} />
        </TabsContent>
        <TabsContent value="serials"><CustomerSerialNumbers serialNumbers={serialNumbers} customerId={id} /></TabsContent>
        <TabsContent value="reports"><CustomerServiceReports reports={reports} customerId={id} /></TabsContent>
        <TabsContent value="documents">
          <Card>
            <CardHeader><CardTitle>Dokumente</CardTitle><CardDescription>Dateien und Dokumente zu diesem Unternehmen</CardDescription></CardHeader>
            <CardContent><DocumentUpload customerId={id} documents={documents} onDocumentsChange={fetchData} /></CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="notes"><CustomerNotesTab notes={notes} setNotes={setNotes} onSave={handleSaveGeneralNotes} saving={savingGeneralNotes} /></TabsContent>
        <TabsContent value="conversation">
          <CustomerConversationTab entries={conversationEntries} onAdd={handleAddConversationNote} onUpdate={handleUpdateConversationNote} onDelete={handleDeleteConversationNote} saving={savingNotes} onRequestFollowup={openFollowupTask} />
        </TabsContent>
        <TabsContent value="comments"><CustomerCommentsTab entityId={id} /></TabsContent>
        <TabsContent value="history"><CustomerAuditTab entityId={id} /></TabsContent>
      </Tabs>

      {/* Sidepanel: Schnellbearbeitung Kontaktdaten */}
      <Sheet open={sidePanelOpen} onOpenChange={setSidePanelOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="quick-edit-panel">
          <SheetHeader>
            <SheetTitle>Kontaktdaten bearbeiten</SheetTitle>
            <SheetDescription>Adresse und Kontaktangaben schnell aktualisieren.</SheetDescription>
          </SheetHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-1.5"><Label htmlFor="q-address">Adresse</Label><Input id="q-address" value={quickForm.address} onChange={(e) => setQuickForm((f) => ({ ...f, address: e.target.value }))} data-testid="quick-address" /></div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1.5"><Label htmlFor="q-plz">PLZ</Label><Input id="q-plz" value={quickForm.postal_code} onChange={(e) => setQuickForm((f) => ({ ...f, postal_code: e.target.value }))} data-testid="quick-plz" /></div>
              <div className="col-span-2 space-y-1.5"><Label htmlFor="q-city">Ort</Label><Input id="q-city" value={quickForm.city} onChange={(e) => setQuickForm((f) => ({ ...f, city: e.target.value }))} data-testid="quick-city" /></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2 space-y-1.5"><Label htmlFor="q-phone">Telefon</Label><Input id="q-phone" value={quickForm.phone} onChange={(e) => setQuickForm((f) => ({ ...f, phone: e.target.value }))} data-testid="quick-phone" /></div>
              <div className="space-y-1.5"><Label htmlFor="q-ext">Durchwahl</Label><Input id="q-ext" value={quickForm.phone_extension} onChange={(e) => setQuickForm((f) => ({ ...f, phone_extension: e.target.value }))} data-testid="quick-ext" /></div>
            </div>
            <div className="space-y-1.5"><Label htmlFor="q-email">E-Mail</Label><Input id="q-email" type="email" value={quickForm.email} onChange={(e) => setQuickForm((f) => ({ ...f, email: e.target.value }))} data-testid="quick-email" /></div>
          </div>
          <SheetFooter>
            <Button variant="outline" onClick={() => setSidePanelOpen(false)} data-testid="quick-cancel-btn">Abbrechen</Button>
            <Button onClick={handleSaveQuick} disabled={savingQuick} data-testid="quick-save-btn">{savingQuick ? "Speichern…" : "Speichern"}</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <CustomerFormDialog open={editCustomerDialogOpen} onOpenChange={setEditCustomerDialogOpen} editCustomer={customer} formData={customerForm} setFormData={setCustomerForm} categories={CATEGORIES} onSubmit={handleSaveCustomer} saving={savingCustomer} users={users} teams={teams} />

      <TaskDialog
        open={taskDialogOpen}
        onOpenChange={setTaskDialogOpen}
        editTask={null}
        formData={taskForm}
        setFormData={setTaskForm}
        onSubmit={handleTaskSubmit}
        saving={taskSaving}
        users={users}
        customers={customer ? [customer] : []}
      />
    </div>
  );
}
